import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
// src/contexts/ConfirmContext.tsx
// Promise-based confirmation dialog, the styled replacement for window.confirm.
// Usage:
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete class?', message: '…', variant: 'danger', confirmLabel: 'Delete' })) { … }

import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';

export interface ConfirmOptions {
  title?: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
}

type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options = {}) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        isOpen={opts !== null}
        onClose={() => settle(false)}
        title={opts?.title || 'Are you sure?'}
        size="sm"
        showCloseButton={false}
        footer={
          <>
            <Button
              variant={opts?.variant === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
              className="w-full sm:w-auto sm:ml-3"
            >
              {opts?.confirmLabel || 'Confirm'}
            </Button>
            <Button variant="secondary" onClick={() => settle(false)} className="mt-3 sm:mt-0 w-full sm:w-auto">
              {opts?.cancelLabel || 'Cancel'}
            </Button>
          </>
        }
      >
        {opts?.message != null && <div className="text-body-sm text-ink-700">{opts.message}</div>}
      </Modal>
    </ConfirmContext.Provider>
  );
};

/** Access the promise-based confirm dialog. Must be inside <ConfirmProvider>. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
